"use client";

import { c as _c } from "react/compiler-runtime";
import type { Collection } from "@/features/collections/types";
import { parseMeta } from "@/features/collections/utils";
import { useLikeToggle } from "@/features/likes/useLikeToggle";
import { useWatchlistToggle } from "@/features/watchlist/useWatchlistToggle";
import { chainKey, chainName, TILE_PLACEHOLDER } from "@/features/home-v2/tiles";
import { sanitizeUrl } from "@/lib/helpers";

/**
 * One read of a collection for every /collection surface — the card, the
 * ledger row and the spotlight read the same fields the same way, so they
 * cannot disagree about a name, a link or a chain. The link carries
 * `network_id`, as the legacy card's and row's did.
 */
export function toCollectionView(c: Collection, lang: "fa" | "en") {
  const isFa = lang === "fa";
  const meta = parseMeta(c.meta);
  return {
    id: c.id,
    href: `/${lang}/collection/${c.contract_address}?network_id=${c.network}`,
    name: isFa && meta.fa_name ? meta.fa_name : c.name,
    banner: sanitizeUrl(c.banner) || sanitizeUrl(c.logo) || TILE_PLACEHOLDER,
    logo: sanitizeUrl(c.logo) || null,
    verified: c.blue_check === 1,
    creator: c.user?.name || c.user?.username || "",
    handle: c.user?.username ? String(c.user.username).replace(/^@/, "") : "",
    avatar: sanitizeUrl(c.user?.profile?.cover ?? undefined) || null,
    chain: chainName(c.network_name, isFa),
    chainKey: chainKey(c.network_name),
    standard: c.standard ? `ERC-${c.standard}` : "",
    created: c.created_at ? new Date(c.created_at).toLocaleDateString(isFa ? "fa-IR" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    }) : ""
  };
}
export type CollectionModel = ReturnType<typeof toCollectionView>;

/**
 * Like and watchlist for one collection — the same two hooks, options and
 * messages the legacy card used. Both hooks share state through the
 * interaction store, so a toggle in the grid is already reflected when the
 * table mounts the same collection.
 */
export function useCollectionToggles(c, lang) {
  const $ = _c(23);
  const isFa = lang === "fa";
  const t0 = c.current_user?.like ?? false;
  const t1 = c.likes?.count ?? 0;
  const t2 = isFa ? "\u062E\u0637\u0627 \u062F\u0631 \u062B\u0628\u062A \u067E\u0633\u0646\u062F" : "Failed to update like";
  let t3;
  if ($[0] !== c.id || $[1] !== t0 || $[2] !== t1 || $[3] !== t2) {
    t3 = {
      modelType: "collection",
      modelId: c.id,
      initialLiked: t0,
      initialCount: t1,
      errorMessage: t2
    };
    $[0] = c.id;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const like = useLikeToggle(t3);
  const t4 = c.current_user?.watchlist ?? false;
  const t5 = isFa ? "\u0628\u0647 \u0648\u0627\u0686\u200C\u0644\u06CC\u0633\u062A \u0627\u0636\u0627\u0641\u0647 \u0634\u062F" : "Added to watchlist";
  const t6 = isFa ? "\u0627\u0632 \u0648\u0627\u0686\u200C\u0644\u06CC\u0633\u062A \u062D\u0630\u0641 \u0634\u062F" : "Removed from watchlist";
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = {
      added: t5,
      removed: t6
    };
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = isFa ? "\u062E\u0637\u0627 \u062F\u0631 \u0627\u0646\u062C\u0627\u0645 \u0639\u0645\u0644\u06CC\u0627\u062A" : "Operation failed";
  let t9;
  if ($[8] !== c.id || $[9] !== t4 || $[10] !== t7 || $[11] !== t8) {
    t9 = {
      modelType: "collection",
      modelId: c.id,
      initialWatchlisted: t4,
      successMessage: t7,
      errorMessage: t8
    };
    $[8] = c.id;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const watch = useWatchlistToggle(t9);
  const t10 = typeof like.count === "number" ? like.count : c.likes?.count ?? 0;
  const t11 = isFa ? like.liked ? "\u0628\u0631\u062F\u0627\u0634\u062A\u0646 \u0644\u0627\u06CC\u06A9" : "\u0644\u0627\u06CC\u06A9" : like.liked ? "Unlike" : "Like";
  const t12 = isFa ? watch.watchlisted ? "\u062D\u0630\u0641 \u0627\u0632 \u0648\u0627\u0686\u200C\u0644\u06CC\u0633\u062A" : "\u0627\u0641\u0632\u0648\u062F\u0646 \u0628\u0647 \u0648\u0627\u0686\u200C\u0644\u06CC\u0633\u062A" : watch.watchlisted ? "Remove from watchlist" : "Add to watchlist";
  let t13;
  if ($[13] !== like.isPending || $[14] !== like.liked || $[15] !== like.toggle || $[16] !== t10 || $[17] !== t11 || $[18] !== t12 || $[19] !== watch.isPending || $[20] !== watch.toggle || $[21] !== watch.watchlisted) {
    t13 = {
      liked: like.liked,
      likeCount: t10,
      toggleLike: like.toggle,
      likePending: like.isPending,
      watched: watch.watchlisted,
      toggleWatch: watch.toggle,
      watchPending: watch.isPending,
      likeLabel: t11,
      watchLabel: t12
    };
    $[13] = like.isPending;
    $[14] = like.liked;
    $[15] = like.toggle;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = watch.isPending;
    $[20] = watch.toggle;
    $[21] = watch.watchlisted;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}